import { Credentials } from '../models/credentials';
import { Authentication } from '../models/authentication.model';
import { environment } from '../../../../environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import decode from 'jwt-decode';
import { map } from 'rxjs/operators';
import { User } from 'src/app/models/user/user.model';
import { rememberMeLogin } from 'src/app/app.constants';
import { MatDialog } from '@angular/material/dialog';

const ACCESS_TOKEN = 'access_token';
const REFRESH_TOKEN = 'refresh_token';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    public http: HttpClient, 
    public router: Router,
    private dialog: MatDialog
  ) {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === ACCESS_TOKEN && event.newValue === null) {
        this.logout();
      }
    });
  }

  handleStorage() {
    const rememberMe = localStorage.getItem(rememberMeLogin) === 'true';
    return rememberMe ? localStorage : sessionStorage;
  }

  login(credentials: Credentials): Observable<Authentication> {
    const body = new HttpParams()
      .set('grant_type', 'password')
      .set('username', credentials.username)
      .set('password', credentials.password)
      .set('user_type', 'staff')
      .set('verification_code', String(credentials.verification_code))
      .set('scope', 'admin');
    const headers = new HttpHeaders()
      .set('Authorization', 'Basic ' + environment.api_key)
      .set('Content-Type', 'application/x-www-form-urlencoded');
    return this.http
      .post(`${environment.api_url}/oauth/token`, body, {
        headers
      })
      .pipe(
        map((response: Authentication) => {
          this.handleStorage().removeItem(ACCESS_TOKEN);
          this.handleStorage().removeItem(REFRESH_TOKEN);
          return response;
        })
      );
  }

  refreshToken(): Observable<Authentication> {
    const token = this.getToken(REFRESH_TOKEN);
    const body = new HttpParams()
      .set('grant_type', REFRESH_TOKEN)
      .set('refresh_token', token)
      .set('user_type', 'staff')
      .set('scope', 'admin');
    const headers = new HttpHeaders()
      .set('Authorization', 'Basic ' + environment.api_key)
      .set('Content-Type', 'application/x-www-form-urlencoded');
    return this.http
      .post(`${environment.api_url}/oauth/token`, body, {
        headers
      })
      .pipe(
        map((response: Authentication) => {
          return response;
        })
      );
  }

  logout(): void {
    this.dialog.closeAll();
    this.handleStorage().removeItem(ACCESS_TOKEN);
    this.handleStorage().removeItem(REFRESH_TOKEN);
    this.router.navigate(['/login'], { replaceUrl: true });
  }

  setSession(accessToken: string, refreshToken: string): void {
    this.handleStorage().setItem(ACCESS_TOKEN, accessToken);
    this.handleStorage().setItem(REFRESH_TOKEN, refreshToken);
  }

  getToken(type: string = ACCESS_TOKEN): string {
    return this.handleStorage().getItem(type);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();

    if (token) {
      return !this.isTokenExpired();
    } else {
      return false;
    }
  }

  getTokenExpirationDate(token: string): Date {
    const decoded = decode(token);

    if (decoded.exp === undefined) {
      return null;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  isTokenExpired(type: string = ACCESS_TOKEN): boolean {
    const token = this.getToken(type);
    if (!token) return true;

    const date = this.getTokenExpirationDate(token);
    if (date === undefined) return true;
    return !(date.valueOf() > new Date().valueOf());
  }

  hasRole(role: string): boolean {
    const token: any = this.getToken();
    const roleClaim = decode(token).role;
    return roleClaim === role;
  }

  hasPrivilege(privilege: string): boolean {
    const token: any = this.getToken();

    if (token) {
      const privilegesClaim: string[] = decode(token).authorities;
      return (
        privilegesClaim && privilegesClaim.findIndex(x => x === privilege) > -1
      );
    }
    this.logout();
    return false;
  }

  isAuthenticatedOrRefresTokenNotExpired() {
    if (this.isAuthenticated() || !this.isTokenExpired(REFRESH_TOKEN)) {
      return true;
    } else if (
      window.location.pathname !== '/login' &&
      window.location.pathname !== '/'
    ) {
      this.logout();
      return false;
    }
    return false;
  }

  isCurrentUser(user: User): boolean {
    const token: any = this.getToken();
    const email = decode(token).user_name;
    return email === user.email;
  }

  getCurrentUserName(): string {
    const token: any = this.getToken();
    return decode(token).given_name;
  }

  getCurrentUserId(): string {
    const token: any = this.getToken();
    return decode(token).user_id;
  }

  isTfaEnable() {
    const token: any = this.getToken();
    if (token) {
      return decode(token).two_fa_enabled;
    } else {
      return false;
    }
  }
}
